// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Info, ExternalLink, Check, X, ShieldCheck, ChevronDown } from "lucide-react";
import PlayerCompliancePanel from "@/components/admin/verification/PlayerCompliancePanel";
import SendVerificationReminderButton from "@/components/admin/verification/SendVerificationReminderButton";
import VerificationUnavailableNotice from "@/components/rosters/VerificationUnavailableNotice";
import { VERIFICATION_UNAVAILABLE } from "@/lib/verificationStatus";




const expiryPlus365 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 365);
  return d.toISOString();
};

type Filter = "all" | "pending" | "unverified";

export default function OperatorEventVerificationPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [eventName, setEventName] = useState("");
  const [settings, setSettings] = useState<any>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<Record<string, any[]>>({});
  const [loadingTeam, setLoadingTeam] = useState<string | null>(null);
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [manualFor, setManualFor] = useState<any>(null);
  const [nsid, setNsid] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);


  const loadSettings = async () => {
    const { data, error } = await supabase.rpc("get_event_verification_settings", { p_event_id: eventId });
    if (error) throw error;
    setSettings(data || {});
    return data;
  };

  const loadTeams = async () => {
    const { data, error } = await supabase.rpc("get_event_verification_team_summary", { p_event_id: eventId });
    if (error) throw error;
    setTeams(Array.isArray(data) ? data : []);
  };

  const loadTeamPlayers = async (teamId: string) => {
    setLoadingTeam(teamId);
    try {
      const { data, error } = await supabase.rpc("get_team_roster_verification", {
        p_event_id: eventId,
        p_team_id: teamId,
      });
      if (error) throw error;
      setTeamPlayers((prev) => ({ ...prev, [teamId]: Array.isArray(data) ? data : [] }));
    } catch (e: any) {
      toast.error(e.message || "Failed to load team roster");
    } finally {
      setLoadingTeam(null);
    }
  };

  const toggleTeam = (teamId: string) => {
    if (openTeamId === teamId) {
      setOpenTeamId(null);
      setTeamPlayers((prev) => {
        const next = { ...prev };
        delete next[teamId];
        return next;
      });
      setExpandedId(null);
      return;
    }
    setOpenTeamId(teamId);
    setExpandedId(null);
    if (!teamPlayers[teamId]) loadTeamPlayers(teamId);
  };

  // Refresh the currently open team + the team summary counts
  const refreshOpen = async () => {
    await loadTeams();
    if (openTeamId) await loadTeamPlayers(openTeamId);
  };

  const loadAll = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data: ev } = await supabase
        .from("events")
        .select("name, organization_id")
        .eq("id", eventId)
        .maybeSingle();
      setEventName(ev?.name || "");
      setOrgId(ev?.organization_id || null);
      const s = await loadSettings();
      if (s?.enabled) await loadTeams();
    } catch (e: any) {
      toast.error(e.message || "Failed to load verification data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const saveSettings = async (patch: any) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSavingSettings(true);
    try {
      const { error } = await supabase.rpc("set_event_verification_settings", {
        p_event_id: eventId,
        p_enabled: !!next.enabled,
        p_mode: next.mode || "flag",
        p_require_age: next.require_age !== false,
        p_require_grade: !!next.require_grade,
      });
      if (error) throw error;
      const s = await loadSettings();
      if (s?.enabled) await loadTeams();
      else setTeams([]);
    } catch (e: any) {
      toast.error(e.message || "Failed to save settings");
      await loadSettings();
    } finally {
      setSavingSettings(false);
    }
  };

  const statusOf = (row: any) => {
    const requireGrade = !!settings?.require_grade;
    const parts = [row.age];
    if (requireGrade) parts.push(row.grade);
    if (row.is_verified) return "verified";
    if (parts.some((c: any) => c?.status === "pending")) return "pending";
    if (parts.every((c: any) => !c || !c.status)) return "none";
    return "none";
  };

  const visibleTeams = useMemo(() => {
    const q = search.trim().toLowerCase();
    return teams.filter((t: any) => {
      const total = Number(t.total_players) || 0;
      const verified = Number(t.verified_count) || 0;
      const pending = Number(t.pending_count) || 0;
      if (q && !String(t.team_name || "").toLowerCase().includes(q)) return false;
      if (filter === "pending") return pending > 0;
      if (filter === "unverified") return verified < total;
      return true;
    });
  }, [teams, search, filter]);


  const review = async (credentialId: string, status: "cleared" | "failed") => {
    setBusyId(credentialId);
    try {
      const { error } = await supabase.rpc("review_player_verification", {
        p_credential_id: credentialId,
        p_status: status,
        p_expires_at: status === "cleared" ? expiryPlus365() : null,
      });
      if (error) throw error;
      toast.success(status === "cleared" ? "Player verified" : "Verification rejected");
      await refreshOpen();
    } catch (e: any) {
      toast.error(e.message || "Review failed");
    } finally {
      setBusyId(null);
    }
  };

  const openDocument = async (credentialId: string) => {
    const { data } = await supabase
      .from("compliance_credentials")
      .select("document_url")
      .eq("id", credentialId)
      .maybeSingle();
    if (data?.document_url) window.open(data.document_url, "_blank", "noopener");
    else toast.info("No document uploaded for this player");
  };

  const markVerified = async () => {
    if (!manualFor) return;
    const row = manualFor;
    setBusyId(row.player_id);
    try {
      let credentialId = row.age?.credential_id;
      if (!credentialId) {
        const { data, error } = await supabase.rpc("submit_player_verification", {
          p_org_id: orgId,
          p_player_id: row.player_id,
          p_credential_type: "age_verification",
          p_external_ref: nsid || null,
        });
        if (error) throw error;
        credentialId = (data as any)?.credential_id || (data as any)?.id || data;
      }
      if (!credentialId) throw new Error("Could not create a verification record for this player");
      const { error: rErr } = await supabase.rpc("review_player_verification", {
        p_credential_id: credentialId,
        p_status: "cleared",
        p_expires_at: expiryPlus365(),
      });
      if (rErr) throw rErr;
      toast.success("Player marked verified");
      setManualFor(null);
      setNsid("");
      await refreshOpen();
    } catch (e: any) {
      toast.error(e.message || "Failed to mark verified");
    } finally {
      setBusyId(null);
    }
  };

  const StatusBadge = ({ row }: { row: any }) => {
    const s = statusOf(row);
    if (s === "verified")
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Verified</Badge>;
    if (s === "pending")
      return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending review</Badge>;
    return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Not submitted</Badge>;
  };

  const totals = useMemo(() => {
    return teams.reduce(
      (acc: any, t: any) => ({
        total: acc.total + (Number(t.total_players) || 0),
        verified: acc.verified + (Number(t.verified_count) || 0),
      }),
      { total: 0, verified: 0 }
    );
  }, [teams]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <BadgeCheck className="h-6 w-6 text-secondary" /> Player Verification
          </h1>
          <p className="text-sm text-muted-foreground">{eventName}</p>
        </div>
        {settings?.enabled && !VERIFICATION_UNAVAILABLE && <SendVerificationReminderButton eventId={eventId} />}
      </div>

      {VERIFICATION_UNAVAILABLE && <VerificationUnavailableNotice />}




      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-base">Require player verification for this event</Label>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Info className="h-3.5 w-3.5" />
                    Flag = unverified players are highlighted but can still check in. Block = unverified players can't be
                    checked in.
                  </p>
                </div>
                <Switch
                  checked={!!settings?.enabled}
                  disabled={savingSettings}
                  onCheckedChange={(v) => saveSettings({ enabled: v })}
                />
              </div>

              {settings?.enabled && (
                <div className="grid gap-4 md:grid-cols-3 pt-2 border-t">
                  <div className="space-y-1.5">
                    <Label>Mode</Label>
                    <Select value={settings?.mode || "flag"} onValueChange={(v) => saveSettings({ mode: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flag">Flag only</SelectItem>
                        <SelectItem value="block">Hard block</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 md:pt-7">
                    <Checkbox
                      id="require_age"
                      checked={settings?.require_age !== false}
                      onCheckedChange={(v) => saveSettings({ require_age: !!v })}
                    />
                    <Label htmlFor="require_age">Verify age</Label>
                  </div>
                  <div className="flex items-center gap-2 md:pt-7">
                    <Checkbox
                      id="require_grade"
                      checked={!!settings?.require_grade}
                      onCheckedChange={(v) => saveSettings({ require_grade: !!v })}
                    />
                    <Label htmlFor="require_grade">Verify grade</Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {settings?.enabled && (
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    <span className="font-semibold">
                      {totals.verified} of {totals.total} verified · {teams.length} teams
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {(["all", "pending", "unverified"] as Filter[]).map((f) => (
                      <Button
                        key={f}
                        size="sm"
                        variant={filter === f ? "default" : "outline"}
                        onClick={() => setFilter(f)}
                      >
                        {f === "all" ? "All" : f === "pending" ? "Pending" : "Unverified"}
                      </Button>
                    ))}
                  </div>
                </div>

                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search teams…"
                  className="max-w-sm"
                />

                {visibleTeams.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">No teams match this filter.</p>
                )}

                <div className="space-y-2">
                  {visibleTeams.map((t: any) => {
                    const total = Number(t.total_players) || 0;
                    const verified = Number(t.verified_count) || 0;
                    const pending = Number(t.pending_count) || 0;
                    const dot =
                      total > 0 && verified === total
                        ? "bg-emerald-500"
                        : pending > 0
                        ? "bg-amber-500"
                        : "bg-red-500";
                    const isOpen = openTeamId === t.team_id;
                    const players = teamPlayers[t.team_id] || [];
                    return (
                      <div key={t.team_id} className="border rounded-lg">
                        <button
                          type="button"
                          onClick={() => toggleTeam(t.team_id)}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/50 rounded-lg"
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
                            <span className="font-medium truncate">{t.team_name || "Unassigned"}</span>
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-sm text-muted-foreground">
                              {verified} / {total} verified
                            </span>
                            <ChevronDown className={`h-4 w-4 transition ${isOpen ? "rotate-180" : ""}`} />
                          </span>
                        </button>

                        {isOpen && (
                          <div className="border-t divide-y">
                            {loadingTeam === t.team_id && <Skeleton className="h-16 w-full m-4" />}
                            {loadingTeam !== t.team_id && players.length === 0 && (
                              <p className="text-sm text-muted-foreground py-6 text-center">
                                No players on this roster.
                              </p>
                            )}
                            {players.map((row: any) => {
                              const s = statusOf(row);
                              const credId = row.age?.credential_id;
                              const expanded = expandedId === row.player_id;
                              return (
                                <div key={row.player_id}>
                                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
                                    <div className="min-w-0">
                                      <div className="font-medium truncate">{row.player_name || "Unnamed player"}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {row.grad_year ? `Class of ${row.grad_year}` : "No grad year"}
                                        {row.date_of_birth ? ` · DOB on file` : " · No DOB"}
                                        {row.checked_in ? " · Checked in" : ""}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <StatusBadge row={row} />
                                      {s === "pending" && credId && (
                                        <>
                                          <Button size="sm" variant="outline" onClick={() => openDocument(credId)}>
                                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Document
                                          </Button>
                                          <Button
                                            size="sm"
                                            disabled={busyId === credId}
                                            onClick={() => review(credId, "cleared")}
                                          >
                                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            disabled={busyId === credId}
                                            onClick={() => review(credId, "failed")}
                                          >
                                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                                          </Button>
                                        </>
                                      )}
                                      {s !== "verified" && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          disabled={busyId === row.player_id}
                                          onClick={() => {
                                            setManualFor(row);
                                            setNsid("");
                                          }}
                                        >
                                          Mark verified
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setExpandedId(expanded ? null : row.player_id)}
                                      >
                                        Verification
                                        <ChevronDown
                                          className={`ml-1 h-3.5 w-3.5 transition ${expanded ? "rotate-180" : ""}`}
                                        />
                                      </Button>
                                    </div>
                                  </div>
                                  {expanded && (
                                    <div className="px-4 pb-4">
                                      <PlayerCompliancePanel
                                        playerId={row.player_id}
                                        orgId={settings?.organization_id || orgId}
                                        playerName={row.player_name}
                                        requireGrade={!!settings?.require_grade}
                                        onChanged={refreshOpen}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={!!manualFor} onOpenChange={(o) => !o && setManualFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark {manualFor?.player_name} verified</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Use this after seeing the player's document in person or recording an NSID card number.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="nsid">NSID number (optional)</Label>
              <Input id="nsid" value={nsid} onChange={(e) => setNsid(e.target.value)} placeholder="e.g. 1234567890" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualFor(null)}>
              Cancel
            </Button>
            <Button onClick={markVerified} disabled={busyId === manualFor?.player_id}>
              Mark verified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
